import { createSelector } from 'reselect'
import {
  type PassportCardQuoteParams,
  type PassportCardQuoteRequestParams,
  travellerDetailsFromInsuranceTravellers,
  travellerDetailsFromOccupants,
} from 'lib/insurance/passportCardQuoteUtils'
import { getNoProtectionPassportCardInsuranceItem } from '../view/insurance'
import { getCheckoutInsuranceCountries, getCheckoutInsuranceDates } from './insurance'
import { getCheckoutInsuranceTravellers } from './getCheckoutInsuranceTravellers'
import getCheckoutQuoteSessionId from './getCheckoutQuoteSessionId'
import getPassportCardInsuranceCoverAmountFromCart from './getPassportCardInsuranceCoverAmountFromCart'
import getPassportCardQuoteItems from '../view/itemViews/getPassportCardQuoteItems'
import getEligibleInsuranceLoyaltyTier from 'checkout/selectors/loyalty/getEligibleInsuranceLoyaltyTier'
import { PRODUCT_ID_PASSPORT_CARD } from 'constants/insurance'

export type PassportCardQuoteInsuranceItemInput = Pick<App.Checkout.InsuranceItem,
  'coverageAmount' |
  'countries' |
  'endDate' |
  'excessAmount' |
  'passportCardOptionalCovers' |
  'passportCardTravellers' |
  'startDate'
>

export interface PassportCardCheckoutQuoteRequestParamsProps {
  params?: PassportCardQuoteParams
  insuranceItem?: PassportCardQuoteInsuranceItemInput
  checkoutDates?: {
    startDate?: string
    endDate?: string
  }
  checkoutCountries?: Array<string>
}

interface BuildPassportCardCheckoutQuoteRequestParams {
  params?: PassportCardQuoteParams
  insuranceItem?: PassportCardQuoteInsuranceItemInput
  checkoutDates: {
    startDate?: string
    endDate?: string
  }
  checkoutCountries: Array<string>
  checkoutTravellers: App.Occupants
  passportCardInsuranceCoverAmountFromCart: number | undefined
  passportCardQuoteItems: Array<App.PassportCardQuoteItem>
  currentRegionCode: string
  currentCurrency: string
  sessionId: string
  loyaltyTier?: string
  spoofingAgentId?: string
}

export function buildPassportCardCheckoutQuoteRequestParams({
  params,
  insuranceItem,
  checkoutDates,
  checkoutCountries,
  checkoutTravellers,
  passportCardInsuranceCoverAmountFromCart,
  passportCardQuoteItems,
  currentRegionCode,
  currentCurrency,
  sessionId,
  loyaltyTier,
  spoofingAgentId,
}: BuildPassportCardCheckoutQuoteRequestParams): PassportCardQuoteRequestParams | undefined {
  if (!passportCardInsuranceCoverAmountFromCart || passportCardInsuranceCoverAmountFromCart <= 0) return undefined
  if (!params && !insuranceItem) return undefined

  const quoteParams = params ?? {
    travellersDetails: insuranceItem?.passportCardTravellers?.length
      ? travellerDetailsFromInsuranceTravellers(insuranceItem.passportCardTravellers)
      : travellerDetailsFromOccupants(checkoutTravellers),
    destinationCountries: insuranceItem?.countries ?? (checkoutCountries.length ? checkoutCountries : []),
    tripStartDate: insuranceItem?.startDate ?? checkoutDates.startDate ?? '',
    tripEndDate: insuranceItem?.endDate ?? checkoutDates.endDate ?? '',
    productId: PRODUCT_ID_PASSPORT_CARD,
  }
  const hasCancellation = insuranceItem?.coverageAmount !== undefined
  const hasExcess = insuranceItem?.excessAmount !== undefined
  const filters = hasCancellation || hasExcess ? {
    cancellation_limit: hasCancellation ? insuranceItem?.coverageAmount : undefined,
    excess_limit: hasExcess ? insuranceItem?.excessAmount : undefined,
  } : undefined

  return {
    ...quoteParams,
    totalPrice: passportCardInsuranceCoverAmountFromCart,
    countryCode: currentRegionCode,
    currency: currentCurrency,
    sessionId,
    items: passportCardQuoteItems,
    filters,
    optionalCovers: insuranceItem?.passportCardOptionalCovers,
    loyaltyTier,
    spoofingAgentId,
  }
}

const getPassportCardCheckoutQuoteRequestParams = createSelector(
  (_state: App.State, props?: PassportCardCheckoutQuoteRequestParamsProps) => props?.params,
  (state: App.State, props?: PassportCardCheckoutQuoteRequestParamsProps) =>
    props ? props.insuranceItem : getNoProtectionPassportCardInsuranceItem(state),
  (state: App.State, props?: PassportCardCheckoutQuoteRequestParamsProps) =>
    props?.checkoutDates ?? getCheckoutInsuranceDates(state),
  (state: App.State, props?: PassportCardCheckoutQuoteRequestParamsProps) =>
    props?.checkoutCountries ?? getCheckoutInsuranceCountries(state),
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (state: App.State) => getPassportCardInsuranceCoverAmountFromCart(state),
  (state: App.State) => getPassportCardQuoteItems(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => getCheckoutQuoteSessionId(state),
  (state: App.State) => getEligibleInsuranceLoyaltyTier(state),
  (state: App.State) => state.auth.account.agentId,
  (
    params,
    insuranceItem,
    checkoutDates,
    checkoutCountries,
    checkoutTravellers,
    passportCardInsuranceCoverAmountFromCart,
    passportCardQuoteItems,
    currentRegionCode,
    currentCurrency,
    sessionId,
    loyaltyTier,
    spoofingAgentId,
  ): PassportCardQuoteRequestParams | undefined => buildPassportCardCheckoutQuoteRequestParams({
    params,
    insuranceItem,
    checkoutDates,
    checkoutCountries,
    checkoutTravellers,
    passportCardInsuranceCoverAmountFromCart,
    passportCardQuoteItems,
    currentRegionCode,
    currentCurrency,
    sessionId,
    loyaltyTier,
    spoofingAgentId,
  }),
)

export default getPassportCardCheckoutQuoteRequestParams
